//src/utils/excel.ts
// Ленивая подгрузка XLSX — отдельный чанк
async function loadXLSX() {
  const mod = await import('xlsx');
  return mod;
}

export type ExcelColumn = string | { title: string; key: string };

export type ExcelExportOptions<T> = {
  filename: string;
  sheetName?: string;
  range?: { from?: Date | null; to?: Date | null } | null;
  data?: T[];
  map?: (row: T) => unknown | unknown[];
  columns?: ReadonlyArray<ExcelColumn>;
  /** используется при фильтрации, если range передан, а getTime не задан */
  dateField?: keyof T | 'createdAt';
};

function isInRange(
  ts: number,
  range?: { from?: Date | null; to?: Date | null } | null
) {
  if (!range) return true;
  const { from, to } = range;
  if (from && ts < from.getTime()) return false;
  if (to && ts > to.getTime()) return false;
  return true;
}

function normalizeColumns(cols?: ReadonlyArray<ExcelColumn>) {
  if (!cols?.length) return null as null | { title: string; key: string }[];
  return cols.map((c) => (typeof c === 'string' ? { title: c, key: c } : c));
}

/** Простой экспорт массива объектов (лениво подгружает xlsx) */
export async function exportToExcelAsync<T = unknown>(
  data: T[],
  filename: string,
  sheetName = 'Sheet1'
) {
  const XLSX = await loadXLSX();
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

/** Синхронная оболочка для совместимости со старыми вызовами */
export function exportToExcel<T = unknown>(
  data: T[],
  filename: string,
  sheetName = 'Sheet1'
) {
  void exportToExcelAsync(data, filename, sheetName);
}

/** Расширенный экспорт (лениво подгружает xlsx) */
export async function exportToExcelAdvancedAsync<T = any>(opts: ExcelExportOptions<T>) {
  const {
    filename,
    sheetName = 'Sheet1',
    range = null,
    data = [],
    map,
    columns,
    dateField = 'createdAt' as keyof T,
  } = opts;

  // 1) Фильтрация по диапазону дат (если range есть)
  const filtered = !range
    ? (data as unknown[])
    : (data as unknown[]).filter((row: any) => {
        const v = row?.[dateField as string];
        const ts = v ? new Date(v).getTime() : NaN;
        return Number.isFinite(ts) ? isInRange(ts, range) : true;
      });

  // 2) Преобразование строк
  const rowsRaw: unknown[] = [];
  for (const row of filtered) {
    const r = map ? map(row as T) : row;
    if (Array.isArray(r)) rowsRaw.push(...r);
    else rowsRaw.push(r);
  }

  // 3) Порядок и заголовки колонок (если переданы)
  let rows: unknown[] = rowsRaw;
  const cols = normalizeColumns(columns);
  if (cols) {
    rows = rowsRaw.map((r: any) => {
      const out: Record<string, unknown> = {};
      for (const c of cols) out[c.title] = r?.[c.key] ?? '';
      return out;
    });
  }

  const XLSX = await loadXLSX();
  const worksheet = XLSX.utils.json_to_sheet(rows as any[]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

/** Синхронная оболочка для совместимости */
export function exportToExcelAdvanced<T = any>(opts: ExcelExportOptions<T>) {
  void exportToExcelAdvancedAsync(opts);
}
